import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// Founder test mode: a browser signed in to /admin sends its admin key so the
// Buddy maker skips the daily cap and the printable is free.
export const ADMIN_KEY_STORAGE = "pb_admin_key_v1";
const testModeHeaders = () => {
  const key = typeof window !== "undefined" ? localStorage.getItem(ADMIN_KEY_STORAGE) : null;
  return key ? { "X-Admin-Key": key } : {};
};
export const isTestMode = () => Boolean(testModeHeaders()["X-Admin-Key"]);

export const fetchBuddies = () => api.get("/buddies").then((r) => r.data);
export const fetchPublicBuddy = (id) => api.get(`/buddies/${id}/public`).then((r) => r.data);
export const fetchRecipes = () => api.get("/recipes").then((r) => r.data);
export const fetchRecipe = (id) => api.get(`/recipes/${id}`).then((r) => r.data);
export const fetchProducts = () => api.get("/products").then((r) => r.data);
export const submitEmail = (email, source = "homepage") =>
  api.post("/email-signup", { email, source }).then((r) => r.data);
// Starts a Buddy drawing job and polls until the drawn-and-checked Buddy is ready.
// Errors are shaped like axios errors so callers can keep reading err.response.data.detail.
const buddyJobError = (detail) => Object.assign(new Error(detail), { response: { data: { detail } } });
export const createCustomBuddy = async (payload, onProgress) => {
  const { data } = await api.post("/custom-buddy", payload, { headers: testModeHeaders() });
  const deadline = Date.now() + 4 * 60 * 1000;
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, 2500));
    let job;
    try {
      job = (await api.get(`/custom-buddy/jobs/${data.job_id}`)).data;
    } catch (err) {
      if (err?.response?.status === 404) throw err;
      continue;
    }
    onProgress?.(job);
    if (job.status === "done") return job.buddy;
    if (job.status === "failed") throw buddyJobError(job.error);
  }
  throw buddyJobError("Your Buddy is taking longer than usual. Please try again in a moment.");
};
export const createCheckout = (payload) =>
  api.post("/checkout/session", payload).then((r) => r.data);
export const previewCart = (items, coupon, extras = {}) =>
  api
    .post("/cart/preview", {
      items,
      coupon,
      recovery_token: extras.recovery_token,
      recovery_session_id: extras.recovery_session_id,
    })
    .then((r) => r.data);
export const applyPilot = (payload) =>
  api.post("/pilot/apply", payload).then((r) => r.data);
export const reserveInteractiveSystem = (payload) =>
  api.post("/preorders/interactive", payload).then((r) => r.data);
export const pilotStats = () =>
  api.get("/pilot/stats").then((r) => r.data);
export const getCheckoutStatus = (session_id) =>
  api.get(`/checkout/status/${session_id}`).then((r) => r.data);
export const generateShoppingList = (days, custom_buddies) =>
  api
    .post("/shopping-list", { days, custom_buddies })
    .then((r) => r.data);
export const saveMealPlan = (days, email, custom_buddies, items_have) =>
  api
    .post("/meal-plan", { days, email, custom_buddies, items_have })
    .then((r) => r.data);
export const accessLookup = (email) =>
  api.post("/access/lookup", { email }).then((r) => r.data);

export const fetchShippingRates = (subtotal) =>
  api.get("/shipping/rates", { params: { subtotal } }).then((r) => r.data);

export const createDigitalPrintCheckout = (payload) =>
  api.post("/digital-print/checkout", payload).then((r) => r.data);

export const createColoringPageCheckout = ({ custom_buddy_id, email, origin_url }) =>
  api
    .post("/digital-print/checkout", {
      custom_buddy_id,
      email,
      origin_url,
      product_id: "buddy_coloring_page",
    })
    .then((r) => r.data);

export const digitalPrintDownloadUrl = (sessionId) =>
  `${API}/digital-print/download/${sessionId}`;

// --- Simplified Buddy Pack validation offer (/create-your-plate-buddy) ---
export const createSimpleDigitalPackCheckout = ({ custom_buddy_id, email, origin_url }) =>
  api
    .post("/digital-print/checkout", {
      custom_buddy_id,
      email,
      origin_url,
      product_id: "simple_digital_pack",
    })
    .then((r) => r.data);

// Tester bypass — beta testers redeem a code for a free copy of the PDFs.
export const redeemTesterCode = ({ code, custom_buddy_id, email }) =>
  api
    .post("/tester/redeem", { code, custom_buddy_id, email }, { headers: testModeHeaders() })
    .then((r) => r.data);

// Free Buddy QR code — one free printable pack per email.
export const checkFreeBuddyCode = (code) =>
  api.get(`/free-buddy/${encodeURIComponent(code)}`).then((r) => r.data);
export const claimFreeBuddy = ({ code, custom_buddy_id, email, join_list }) =>
  api.post("/free-buddy/claim", { code, custom_buddy_id, email, join_list }).then((r) => r.data);

// Regenerate the recipe on a custom Buddy — used by the meal planner's
// "Refresh recipe" button when a legacy Buddy in localStorage still holds
// a mismatched template recipe.
export const refreshBuddyRecipe = (buddyId) =>
  api.post(`/custom-buddy/${buddyId}/refresh-recipe`).then((r) => r.data);

// --- Cross-device meal plan storage (signed-in users only) ---
// Attaches the session token as a Bearer header so the calls also work in
// contexts where the httpOnly cookie is stripped (e.g. some mobile in-app
// browsers). The token lives in localStorage under `pb_session_token`.
const LS_TOKEN = "pb_session_token";
const authHeaders = () => {
  const t = typeof window !== "undefined" ? localStorage.getItem(LS_TOKEN) : null;
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export const getMyPlan = () =>
  api
    .get("/me/plan", { headers: authHeaders(), withCredentials: true })
    .then((r) => r.data);

export const saveMyPlan = ({ days, custom_buddies, items_have }) =>
  api
    .post(
      "/me/plan",
      { days, custom_buddies, items_have },
      { headers: authHeaders(), withCredentials: true }
    )
    .then((r) => r.data);
